// What a map's script (maps/<name>.script) sets up when the level starts. The
// game does not run ET's script language; it takes from the "spawn" events
// the parts that decide what the map is at the start: the spawn objective
// (setautospawn) and the entities hidden or shown with setstate.

import type { Entity } from '../entities';

type Statement = string[];

interface ScriptBlock {
  name: string;
  /** Event name ("spawn", "trigger <name>", ...) -> statements. */
  events: { event: string; statements: Statement[] }[];
}

/** Tokens of a script: words, quoted strings and braces, without comments. */
function tokenize(text: string): { token: string; line: number }[] {
  const out: { token: string; line: number }[] = [];
  let line = 1;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === '\n') {
      line++;
      i++;
    } else if (c <= ' ') i++;
    else if (c === '/' && text[i + 1] === '/') while (i < text.length && text[i] !== '\n') i++;
    else if (c === '/' && text[i + 1] === '*') {
      i += 2;
      while (i < text.length && !(text[i] === '*' && text[i + 1] === '/')) if (text[i++] === '\n') line++;
      i += 2;
    } else if (c === '"') {
      const end = text.indexOf('"', i + 1);
      const stop = end < 0 ? text.length : end;
      out.push({ token: text.slice(i + 1, stop), line });
      i = stop + 1;
    } else if (c === '{' || c === '}') {
      out.push({ token: c, line });
      i++;
    } else {
      let j = i;
      while (j < text.length && text[j] > ' ' && text[j] !== '{' && text[j] !== '}' && text[j] !== '"') j++;
      out.push({ token: text.slice(i, j), line });
      i = j;
    }
  }
  return out;
}

/** Blocks "name { event { statement ... } ... }"; a statement is the tokens of one line. */
export function parseMapScript(text: string): ScriptBlock[] {
  const tokens = tokenize(text);
  const blocks: ScriptBlock[] = [];
  let i = 0;
  while (i < tokens.length) {
    const name = tokens[i++].token;
    if (tokens[i]?.token !== '{') continue;
    i++;
    const block: ScriptBlock = { name: name.toLowerCase(), events: [] };
    // events until the block's closing brace
    while (i < tokens.length && tokens[i].token !== '}') {
      const head: string[] = [];
      while (i < tokens.length && tokens[i].token !== '{' && tokens[i].token !== '}') head.push(tokens[i++].token);
      if (tokens[i]?.token !== '{') break;
      i++;
      const statements: Statement[] = [];
      let depth = 1;
      let current: Statement = [];
      let line = -1;
      while (i < tokens.length && depth > 0) {
        const t = tokens[i++];
        if (t.token === '{') depth++;
        else if (t.token === '}') depth--;
        if (depth === 0) break;
        if (t.line !== line && current.length) {
          statements.push(current);
          current = [];
        }
        line = t.line;
        if (t.token !== '{' && t.token !== '}') current.push(t.token);
      }
      if (current.length) statements.push(current);
      block.events.push({ event: head.join(' ').toLowerCase(), statements });
    }
    i++;
    blocks.push(block);
  }
  return blocks;
}

/** Statements of every block's spawn event, game_manager last (its spawn runs after the entities' own). */
function spawnStatements(blocks: ScriptBlock[]): Statement[] {
  const ordered = [...blocks.filter((b) => b.name !== 'game_manager'), ...blocks.filter((b) => b.name === 'game_manager')];
  return ordered.flatMap((b) => b.events.filter((e) => e.event === 'spawn').flatMap((e) => e.statements));
}

/**
 * The statements the spawn events run, in order, with the "trigger <block>
 * <event>" calls among them followed into the called events.
 */
function startStatements(blocks: ScriptBlock[]): Statement[] {
  const out: Statement[] = [];
  const run = (statements: Statement[], depth: number) => {
    for (const s of statements) {
      out.push(s);
      if (s[0]?.toLowerCase() !== 'trigger' || s.length < 3 || depth > 4) continue;
      const block = blocks.find((b) => b.name === s[1].toLowerCase());
      const event = block?.events.find((e) => e.event === `trigger ${s[2].toLowerCase()}`);
      if (event) run(event.statements, depth + 1);
    }
  };
  run(spawnStatements(blocks), 0);
  return out;
}

export interface ScriptStart {
  /** setautospawn "<objective>" <team>: team (0 Axis, anything else Allies) -> objective name. */
  autoSpawns: Map<number, string>;
  /** alertentity <targetname>: how many times (team spawn points toggle active with each use). */
  alerted: Map<string, number>;
}

/** What the spawn events set up for the team spawns. */
export function scriptStart(script: string): ScriptStart {
  const autoSpawns = new Map<number, string>();
  const alerted = new Map<string, number>();
  for (const s of startStatements(parseMapScript(script))) {
    const cmd = s[0]?.toLowerCase();
    if (cmd === 'setautospawn' && s.length >= 3) autoSpawns.set(Math.trunc(Number(s[2])) === 0 || Number.isNaN(Number(s[2])) ? 0 : 1, s[1]);
    else if (cmd === 'alertentity' && s.length >= 2) alerted.set(s[1], (alerted.get(s[1]) ?? 0) + 1);
  }
  return { autoSpawns, alerted };
}

/** setautospawn "<objective>" <team> at the start: team (0 Axis, 1 Allies) -> objective description. */
export function autoSpawns(script: string): Map<number, string> {
  return scriptStart(script).autoSpawns;
}

/**
 * Entities (by index) a map starts without: setstate <targetname> invisible
 * (or underconstruction) in a spawn event, unless set back to default. They
 * neither collide, trigger nor draw (G_SetEntState unlinks them).
 */
export function hiddenEntities(entities: Entity[], script: string | null | undefined): Set<number> {
  const hidden = new Set<number>();
  if (!script) return hidden;
  const states = new Map<string, string>();
  for (const s of spawnStatements(parseMapScript(script))) {
    if (s[0]?.toLowerCase() === 'setstate' && s.length >= 3) states.set(s[1], s[2].toLowerCase());
  }
  entities.forEach((e, i) => {
    const state = e.targetname ? states.get(e.targetname) : undefined;
    if (state === 'invisible' || state === 'underconstruction') hidden.add(i);
  });
  return hidden;
}
